
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sparkles } from "lucide-react";
import PageLayout from "../components/shared/PageLayout";

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Fortune Cookies | Daily Fortune</title>
        <meta name="description" content="Learn about the history and significance of fortune cookies, their cultural impact, and how our daily fortune cookie readings can bring insight and joy to your day." />
        <meta name="keywords" content="fortune cookies, daily fortune, cookie wisdom, fortune telling, luck, inspiration" />
        <meta property="og:title" content="About Fortune Cookies | Daily Fortune" />
        <meta property="og:description" content="Discover the fascinating world of fortune cookies and how they can bring daily inspiration and wisdom to your life." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/about" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Fortune Cookies | Daily Fortune" />
        <meta name="twitter:description" content="Learn about fortune cookies and get your daily dose of wisdom and inspiration with our virtual fortune cookie experience." />
        <link rel="canonical" href="/about" />
      </Helmet>

      <PageLayout showBackButton={false}>
        <motion.div 
          className="space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <motion.h1 
              className="text-4xl font-light mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              About Fortune Cookies
            </motion.h1>
            <motion.p
              className="text-muted-foreground text-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Discover the magic and wisdom hidden within these sweet treats
            </motion.p>
          </div>

          {/* Featured Button - Visually attractive fortune button */}
          <motion.div 
            className="flex justify-center my-16"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            whileHover={{ scale: 1.05 }}
          >
            <Link to="/fortune">
              <Button size="lg" className="bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-white py-8 px-12 rounded-full shadow-lg hover:shadow-xl transition-all">
                <Sparkles className="mr-2 h-6 w-6" />
                <span className="text-xl">Get Your Fortune Now</span>
              </Button>
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <h2 className="text-2xl font-medium">The History of Fortune Cookies</h2>
              <p className="text-muted-foreground">
                Fortune cookies, those crisp, folded cookies with hidden messages inside, have become an iconic part of American Chinese restaurant culture. 
                Despite their association with Chinese cuisine, fortune cookies actually originated in Japan before becoming popular in the United States 
                in the early 20th century.
              </p>
              <p className="text-muted-foreground">
                The modern fortune cookie was likely introduced to the United States by Japanese immigrants, but it was Chinese restaurants 
                that popularized them as a dessert, particularly after World War II. Today, billions of fortune cookies are produced annually, 
                primarily in the United States.
              </p>
            </motion.div>

            <motion.div
              className="rounded-xl overflow-hidden aspect-square md:aspect-auto h-full bg-muted relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.0, duration: 0.8 }}
            >
              <img 
                src="/lovable-uploads/52720b64-5ddc-435c-b502-343d03269436.png" 
                alt="Fortune cookies on a decorative plate in a traditional Japanese setting" 
                className="w-full h-full object-contain md:object-cover p-2"
              />
            </motion.div>
          </div>

          <motion.div
            className="mt-12 space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            <h2 className="text-2xl font-medium">The Significance of Fortunes</h2>
            <p className="text-muted-foreground">
              Fortune cookies are more than just sweet treats; they're vessels of wisdom, inspiration, and sometimes humor. 
              The small slips of paper inside contain messages that can range from prophetic predictions to practical advice and 
              philosophical insights.
            </p>
            <p className="text-muted-foreground">
              While not deeply rooted in ancient divination practices, these simple messages often arrive at just the right moment, 
              offering guidance, perspective, or a smile when needed most. They serve as small reminders that sometimes wisdom 
              comes from unexpected places and in bite-sized portions.
            </p>
            <p className="text-muted-foreground">
              Some people take their fortune cookie messages to heart, seeing them as signs or gentle nudges from the universe. 
              Others collect them, share them with friends, or use them as daily affirmations. Regardless of how they're received, 
              there's something uniquely delightful about cracking open a cookie to reveal your fortune.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 mt-12">
            <motion.div
              className="rounded-xl overflow-hidden aspect-auto h-full bg-muted relative"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.4, duration: 0.8 }}
            >
              <img 
                src="/lovable-uploads/61f937e8-1bb1-403f-b611-76361d0a2d7f.png" 
                alt="Reading a fortune from a cookie in a traditional Japanese room" 
                className="w-full h-full object-contain p-2"
              />
            </motion.div>

            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.6, duration: 0.8 }}
            >
              <h2 className="text-2xl font-medium">Our Daily Fortune Experience</h2>
              <p className="text-muted-foreground">
                In our fast-paced digital world, we've created a virtual fortune cookie experience that brings 
                the same sense of anticipation and delight as the physical version. Our Daily Fortune website offers 
                you a moment of pause and reflection, a daily ritual to look forward to.
              </p>
              <p className="text-muted-foreground">
                Each day, you can crack open a virtual fortune cookie and receive a message curated to inspire, motivate, 
                or simply bring a smile to your face. It's a small but meaningful way to add a touch of magic and 
                mindfulness to your routine.
              </p>
              <p className="text-muted-foreground">
                Whether you're seeking guidance, a positive affirmation, or just a moment of lighthearted fun, 
                our virtual fortune cookies are here to deliver wisdom in the same surprising and delightful way 
                as their physical counterparts ♥
              </p>
            </motion.div>
          </div>

          {/* Second call-to-action button */}
          <motion.div 
            className="text-center mt-16 mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 0.8 }}
          >
            <Link to="/fortune">
              <Button size="lg" className="bg-cookie-dark hover:bg-cookie-light text-white px-8 py-6">
                <Sparkles className="mr-2" />
                Get Your Daily Fortune
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </PageLayout>
    </>
  );
};

export default About;
